import { useState } from "react";
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
} from "reactstrap";

import {
    FiActivity,
    FiArrowRight,
    FiBarChart2,
    FiCheckCircle,
    FiChevronRight,
    FiCloud,
    FiCode,
    FiCpu,
    FiDatabase,
    FiEye,
    FiFileText,
    FiLayers,
    FiMonitor,
    FiPlayCircle,
    FiServer,
    FiSettings,
    FiZap,
} from "react-icons/fi";

import GetInTouch from "../../components/common/Contact/GetInTouch";

import "./IntelligentManufacturingQualityControlPage.css";
import SEO from "../../components/common/seo/SEO";


const IntelligentManufacturingQualityControlPage = () => {

    const [openFaq, setOpenFaq] = useState("");

    const toggleFaq = (id) => {
        setOpenFaq(openFaq === id ? "" : id);
    };


    const prerequisites = [
        {
            icon: <FiCode />,
            title: "Python",
            description:
                "Intermediate level Python knowledge including functions, libraries, and fixing simple errors.",
        },
        {
            icon: <FiDatabase />,
            title: "Data Science",
            description:
                "Basic understanding of cleaning, exploring, and understanding data.",
        },
        {
            icon: <FiCpu />,
            title: "Machine Learning",
            description:
                "Understanding of ML concepts and basic models such as classification or clustering.",
        },
        {
            icon: <FiEye />,
            title: "Computer Vision",
            description:
                "Basic understanding of image processing using tools such as OpenCV.",
        },
        {
            icon: <FiZap />,
            title: "Generative AI",
            description:
                "General awareness of how tools such as ChatGPT or image generation models work.",
        },
    ];


    const technologies = [
        {
            icon: <FiCode />,
            title: "Python",
            description:
                "Core programming language for building the entire pipeline.",
        },
        {
            icon: <FiEye />,
            title: "YOLOv8",
            description:
                "Pre-trained object detection model for high-accuracy defect detection.",
        },
        {
            icon: <FiCpu />,
            title: "Transformers",
            description:
                "Idefics / LLaMA models used for intelligent analytics and report generation.",
        },
        {
            icon: <FiLayers />,
            title: "LabelImg / Roboflow",
            description:
                "Tools for image annotation and dataset preparation.",
        },
        {
            icon: <FiActivity />,
            title: "Albumentations",
            description:
                "Image augmentation techniques used during model training.",
        },
        {
            icon: <FiSettings />,
            title: "PyTorch",
            description:
                "Deep learning framework for training and fine-tuning custom models.",
        },
        {
            icon: <FiCpu />,
            title: "Bitsandbytes",
            description:
                "Model quantization to reduce model size and inference latency.",
        },
        {
            icon: <FiServer />,
            title: "NVIDIA Jetson",
            description:
                "Edge device platform for optimized real-time model deployment.",
        },
        {
            icon: <FiMonitor />,
            title: "Gradio / Streamlit",
            description:
                "Interactive applications for visualizing detections and triggering alerts.",
        },
        {
            icon: <FiCloud />,
            title: "FastAPI / Flask",
            description:
                "Backend frameworks used to serve inference APIs.",
        },
        {
            icon: <FiEye />,
            title: "OpenCV",
            description:
                "Image and video stream handling for real-time scenarios.",
        },
    ];


    const learningObjectives = [
        "Capture, annotate, and prepare image datasets for defect detection tasks.",
        "Fine-tune state-of-the-art models like YOLOv8 or LLaMA on custom tire defect datasets.",
        "Apply image augmentation techniques to improve model generalization.",
        "Quantize and optimize models using tools like bitsandbytes for edge deployment.",
        "Generate inspection reports using GenAI models based on detection outputs.",
        "Deploy models via API and integrate them into real-time applications using Gradio or Streamlit.",
    ];


    const lessonPlan = [
        {
            number: "01",
            title: "Data Collection & Labeling",
            duration: "1 Hour",
            description:
                "Capture tyre images and label them.",
            icon: <FiDatabase />,
        },
        {
            number: "02",
            title: "Model Training & Fine-Tuning",
            duration: "1 Hour",
            description:
                "Use a pre-trained computer vision model such as YOLOv8 or generative AI models such as Idefics / LLaMA and fine-tune it on labeled tyre datasets with augmentations to improve defect detection accuracy.",
            icon: <FiCpu />,
        },
        {
            number: "03",
            title: "Model Optimization with Quantization",
            duration: "1 Hour",
            description:
                "Quantize the trained model, for example to INT8, using tools such as bitsandbytes to reduce size and speed up inference for edge deployment such as NVIDIA Jetson.",
            icon: <FiZap />,
        },
        {
            number: "04",
            title: "GenAI for Analytics",
            duration: "1 Hour",
            description:
                "Generate inspection reports from detection results.",
            icon: <FiFileText />,
        },
        {
            number: "05",
            title: "Real-Time Deployment & Integration",
            duration: "1 Hour",
            description:
                "Integrate the optimized model into an API. Use Gradio or Streamlit to process live feeds and trigger alerts for detected defects.",
            icon: <FiMonitor />,
        },
    ];


    const faqs = [
        {
            id: "faq-1",
            question:
                "I’ve wasted money on courses before. How is this different?",
            answer:
                "This is not just theory. You’ll build a fully working AI-powered IT support automation system — step-by-step, guided by real professionals. It’s practical, project-based, and portfolio-ready.",
        },
        {
            id: "faq-2",
            question:
                "I’m not confident in coding yet. Can I still join?",
            answer:
                "If you’ve completed some basic programming (especially through our beginner courses), you’re good to go! If not, start with our foundation courses, then return for this project.",
        },
        {
            id: "faq-3",
            question:
                "Is this project useful for job interviews or placements?",
            answer:
                "Absolutely. You’ll finish with a deployable IT automation project, integrated with GitHub and real-time logging — a great addition to your resume and GitHub portfolio.",
        },
        {
            id: "faq-4",
            question:
                "Is this course beginner-friendly?",
            answer:
                "It’s beginner-friendly if you’ve completed basic to intermediate programming. If not, take our intro courses first — they’re designed to prepare you for this exact project.",
        },
        {
            id: "faq-5",
            question:
                "What tools do I need before starting?",
            answer:
                "Just install Python, Node.js, and VS Code — we’ll guide you through setup in the first session. Everything else will be covered during the course.",
        },
        {
            id: "faq-6",
            question:
                "Is this really worth my time?",
            answer:
                "In just 10 focused hours, you’ll learn email automation, GitHub API, WebSocket, and React integration — tools used by real tech teams today.",
        },
        {
            id: "faq-7",
            question:
                "Will I receive a certificate after the course?",
            answer:
                "Yes, you will receive an industry-recognized certificate.",
        },
        {
            id: "faq-8",
            question:
                "Are you looking to hire candidates for companies?",
            answer:
                "Yes, we specialize in helping businesses find and hire the right talent. Our HR and recruitment services are tailored to meet your company’s specific hiring needs. Contact us to learn how we can support your hiring process.",
        },
    ];


    const relatedProjects = [
        {
            icon: <FiServer />,
            title: "IT Support Agentic AI",
            description:
                "Build an AI-powered IT support automation system that processes email-based IT requests, identifies intents (e.g., GitHub access/revoke), creates tickets in a ticketing system (e.g., mock ADO/ServiceNow), performs GitHub actions, and displays activity logs in a frontend.",
            link: "/replace-it-help-desk-with-automated-ticketing-system-using-ai",
        },
        {
            icon: <FiDatabase />,
            title: "RAG (Retrieval Argument Generation)",
            description:
                "Optimized large language model outputs by integrating authoritative external knowledge bases, enhancing accuracy and relevance beyond pre-trained data sources.",
            link: "/ai-ml-project-rag-retrieval-argument-generation",
        },
        {
            icon: <FiEye />,
            title: "Image Classification Using Naive & Deep Learning Techniques",
            description:
                "This project focuses on developing an efficient image classification system using a classical computer vision pipeline. The classification is performed using Support Vector Machines (SVM) after extracting meaningful features from input images using the Histogram of Oriented Gradients (HOG) descriptor.",
            link: "/ai-ml-project-image-classification-deep-learning",
        },
        {
            icon: <FiActivity />,
            title: "Brain Stroke Prediction using OpenCV and CNN",
            description:
                "Developed an AI-based brain stroke prediction system using OpenCV and CNNs to analyze MRI/CT scans. The model automates stroke detection with high accuracy by extracting spatial features from medical images. Built with TensorFlow/Keras, the solution streamlines diagnosis and supports clinical decision-making.",
            link: "/ai-ml-project-brain-stroke-prediction-opencv-cnn",
        },
    ];


    return (
        <>

            <SEO
                title="AI Manufacturing Quality Control Project | YOLOv8 & GenAI | FeesWorth"
                description="Build an AI-powered manufacturing quality control system for tire production using computer vision, YOLOv8, Generative AI, PyTorch, OpenCV, edge AI, and real-time deployment."
                keywords="AI Manufacturing Project, Manufacturing Quality Control AI, Tire Defect Detection, YOLOv8 Project, Computer Vision Manufacturing, Generative AI Manufacturing, AI Quality Control System, Industrial AI Project, Edge AI Project, Smart Manufacturing AI"
                canonical="https://feesworth.com/ai-ml-project-intelligent-manufacturing-quality-control-system-for-tire-production"
            />

            <div className="manufacturing-project-page">

                {/* ================= HERO ================= */}

                <section className="mfg-hero-section">
                    <Container>
                        <Row className="align-items-center g-5">

                            <Col lg="7">
                                <div className="mfg-hero-content">

                                    <Badge className="mfg-hero-badge">
                                        AI / ML PROJECT
                                    </Badge>

                                    <h1>
                                        Intelligent Manufacturing
                                        <span>
                                            Quality Control System
                                        </span>
                                        For Tire Production
                                    </h1>

                                    <p className="mfg-hero-description">
                                        Build an AI-powered quality control system
                                        for tire manufacturing using computer
                                        vision and Generative AI.
                                    </p>

                                    <div className="mfg-hero-tags">
                                        <span>Computer Vision</span>
                                        <span>YOLOv8</span>
                                        <span>Generative AI</span>
                                        <span>Edge AI</span>
                                        <span>AWS</span>
                                    </div>

                                    <div className="mfg-hero-actions">
                                        <a href="#mfg-contact">
                                            <Button
                                                color="primary"
                                                className="mfg-primary-btn"
                                            >
                                                Let's Build This Project
                                                <FiArrowRight />
                                            </Button>
                                        </a>

                                        <a href="#mfg-lesson-plan">
                                            <Button
                                                color="link"
                                                className="mfg-outline-btn"
                                            >
                                                <FiPlayCircle />
                                                View Lesson Plan
                                            </Button>
                                        </a>
                                    </div>

                                </div>
                            </Col>


                            <Col lg="5">
                                <div className="mfg-hero-visual">

                                    <div className="mfg-visual-glow"></div>

                                    <div className="mfg-production-line">

                                        <div className="mfg-visual-header">
                                            <span>
                                                <FiActivity />
                                                LIVE QUALITY MONITOR
                                            </span>

                                            <span className="mfg-live-dot">
                                                LIVE
                                            </span>
                                        </div>

                                        <div className="mfg-tire-preview">
                                            <div className="mfg-tire">
                                                <div className="mfg-tire-inner">
                                                    <div className="mfg-tire-hole"></div>
                                                </div>
                                            </div>

                                            <div className="mfg-detection-box">
                                                <span>DEFECT</span>
                                                <strong>94.8%</strong>
                                            </div>
                                        </div>

                                        <div className="mfg-analysis-grid">

                                            <div>
                                                <small>
                                                    DETECTION
                                                </small>
                                                <strong>
                                                    94.8%
                                                </strong>
                                            </div>

                                            <div>
                                                <small>
                                                    STATUS
                                                </small>
                                                <strong>
                                                    ANALYZED
                                                </strong>
                                            </div>

                                            <div>
                                                <small>
                                                    AI MODEL
                                                </small>
                                                <strong>
                                                    YOLOv8
                                                </strong>
                                            </div>

                                        </div>

                                    </div>

                                    <div className="mfg-floating-card mfg-floating-card-one">
                                        <FiEye />
                                        <div>
                                            <strong>Defect Detection</strong>
                                            <span>Computer Vision</span>
                                        </div>
                                    </div>

                                    <div className="mfg-floating-card mfg-floating-card-two">
                                        <FiFileText />
                                        <div>
                                            <strong>AI Report</strong>
                                            <span>GenAI Analysis</span>
                                        </div>
                                    </div>

                                </div>
                            </Col>

                        </Row>
                    </Container>
                </section>


                {/* ================= PROJECT OVERVIEW ================= */}

                <section className="mfg-section mfg-overview-section">
                    <Container>

                        <div className="mfg-section-heading">
                            <span>PROJECT DESCRIPTION</span>
                            <h2>
                                What is this project all about?
                            </h2>
                            <p>
                                Learn to build an AI-powered quality control
                                system for tire manufacturing using computer
                                vision and GenAI.
                            </p>
                        </div>

                        <Row className="align-items-center g-5">

                            <Col lg="6">
                                <div className="mfg-overview-content">

                                    <p>
                                        Learn to Build an AI-powered quality
                                        control system for tire manufacturing
                                        using computer vision and GenAI. This
                                        course covers real-time defect detection
                                        from production line videos and
                                        automated root cause analysis using
                                        generative AI.
                                    </p>

                                    <p>
                                        You’ll deploy the solution on AWS,
                                        enabling smart monitoring and process
                                        optimization. Perfect for professionals
                                        aiming to apply AI in industrial
                                        automation and smart manufacturing.
                                    </p>

                                    <div className="mfg-scope-box">
                                        <div className="mfg-scope-icon">
                                            <FiLayers />
                                        </div>

                                        <div>
                                            <span>PROJECT SCOPE</span>

                                            <p>
                                                An AI-powered quality control
                                                system for tire manufacturing
                                                that uses computer vision and
                                                GenAI for real-time anomaly
                                                detection and automated root
                                                cause analysis.
                                            </p>
                                        </div>
                                    </div>

                                </div>
                            </Col>


                            <Col lg="6">
                                <div className="mfg-pipeline-card">

                                    <div className="mfg-pipeline-title">
                                        <FiActivity />
                                        AI QUALITY CONTROL PIPELINE
                                    </div>

                                    <div className="mfg-pipeline">

                                        <div className="mfg-pipeline-step">
                                            <FiMonitor />
                                            <span>
                                                Production
                                                <small>Video / Images</small>
                                            </span>
                                        </div>

                                        <FiChevronRight className="mfg-pipeline-arrow" />

                                        <div className="mfg-pipeline-step">
                                            <FiEye />
                                            <span>
                                                Detection
                                                <small>YOLOv8</small>
                                            </span>
                                        </div>

                                        <FiChevronRight className="mfg-pipeline-arrow" />

                                        <div className="mfg-pipeline-step">
                                            <FiCpu />
                                            <span>
                                                GenAI
                                                <small>Root Cause</small>
                                            </span>
                                        </div>

                                        <FiChevronRight className="mfg-pipeline-arrow" />

                                        <div className="mfg-pipeline-step">
                                            <FiFileText />
                                            <span>
                                                Report
                                                <small>Analytics</small>
                                            </span>
                                        </div>

                                    </div>

                                </div>
                            </Col>

                        </Row>

                    </Container>
                </section>


                {/* ================= PREREQUISITES ================= */}

                <section className="mfg-section mfg-dark-section">
                    <Container>

                        <div className="mfg-section-heading mfg-light-heading">
                            <span>PREREQUISITES</span>
                            <h2>
                                What You Should Know Before Starting
                            </h2>
                            <p>
                                A foundation in programming, machine learning,
                                computer vision, and Generative AI will help you
                                get the most from this project.
                            </p>
                        </div>

                        <Row className="g-4">

                            {prerequisites.map((item, index) => (
                                <Col
                                    key={index}
                                    md="6"
                                    lg="4"
                                >
                                    <Card className="mfg-info-card">
                                        <CardBody>

                                            <div className="mfg-card-icon">
                                                {item.icon}
                                            </div>

                                            <h3>
                                                {item.title}
                                            </h3>

                                            <p>
                                                {item.description}
                                            </p>

                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}

                        </Row>

                    </Container>
                </section>


                {/* ================= TECHNOLOGIES ================= */}

                <section className="mfg-section">
                    <Container>

                        <div className="mfg-section-heading">
                            <span>TOOLS & TECHNOLOGIES</span>
                            <h2>
                                Technology Stack Used in the Project
                            </h2>
                            <p>
                                Work with modern AI, computer vision,
                                deployment, and edge computing technologies.
                            </p>
                        </div>

                        <Row className="g-4">

                            {technologies.map((item, index) => (
                                <Col
                                    key={index}
                                    sm="6"
                                    lg="4"
                                >
                                    <Card className="mfg-tech-card">
                                        <CardBody>

                                            <div className="mfg-tech-icon">
                                                {item.icon}
                                            </div>

                                            <div>
                                                <h3>
                                                    {item.title}
                                                </h3>

                                                <p>
                                                    {item.description}
                                                </p>
                                            </div>

                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}

                        </Row>

                    </Container>
                </section>


                {/* ================= LEARNING OBJECTIVES ================= */}

                <section className="mfg-section mfg-objectives-section">
                    <Container>

                        <Row className="align-items-center g-5">

                            <Col lg="5">

                                <div className="mfg-objectives-intro">

                                    <span>
                                        LEARNING OBJECTIVES
                                    </span>

                                    <h2>
                                        What You Will Learn
                                    </h2>

                                    <p>
                                        Build an end-to-end AI-powered
                                        manufacturing quality control
                                        solution from data preparation
                                        through real-time deployment.
                                    </p>

                                    <div className="mfg-objective-stat">
                                        <FiBarChart2 />

                                        <div>
                                            <strong>
                                                End-to-End AI Pipeline
                                            </strong>

                                            <span>
                                                From defect detection to
                                                GenAI-powered analytics
                                            </span>
                                        </div>
                                    </div>

                                </div>

                            </Col>


                            <Col lg="7">

                                <div className="mfg-objectives-list">

                                    {learningObjectives.map(
                                        (objective, index) => (
                                            <div
                                                className="mfg-objective-item"
                                                key={index}
                                            >
                                                <span className="mfg-check-icon">
                                                    <FiCheckCircle />
                                                </span>

                                                <p>
                                                    {objective}
                                                </p>
                                            </div>
                                        )
                                    )}

                                </div>

                            </Col>

                        </Row>

                    </Container>
                </section>


                {/* ================= LESSON PLAN ================= */}

                <section
                    className="mfg-section mfg-dark-section"
                    id="mfg-lesson-plan"
                >
                    <Container>

                        <div className="mfg-section-heading mfg-light-heading">
                            <span>LESSON PLAN</span>
                            <h2>
                                Build the Project Step by Step
                            </h2>
                            <p>
                                Five focused modules covering the complete
                                AI-powered manufacturing quality control
                                workflow.
                            </p>
                        </div>


                        <div className="mfg-timeline">

                            {lessonPlan.map((lesson, index) => (
                                <div
                                    className="mfg-timeline-item"
                                    key={index}
                                >

                                    <div className="mfg-timeline-marker">
                                        {lesson.number}
                                    </div>

                                    <div className="mfg-timeline-line"></div>

                                    <Card className="mfg-lesson-card">
                                        <CardBody>

                                            <div className="mfg-lesson-top">

                                                <div className="mfg-lesson-icon">
                                                    {lesson.icon}
                                                </div>

                                                <div>
                                                    <span>
                                                        MODULE {lesson.number}
                                                    </span>

                                                    <h3>
                                                        {lesson.title}
                                                    </h3>
                                                </div>

                                                <Badge className="mfg-duration-badge">
                                                    {lesson.duration}
                                                </Badge>

                                            </div>

                                            <p>
                                                {lesson.description}
                                            </p>

                                        </CardBody>
                                    </Card>

                                </div>
                            ))}

                        </div>

                    </Container>
                </section>


                {/* ================= FAQ ================= */}

                <section className="mfg-section mfg-faq-section">
                    <Container>

                        <div className="mfg-section-heading">
                            <span>FAQ</span>
                            <h2>
                                Frequently Asked Questions
                            </h2>
                            <p>
                                Everything you need to know before starting
                                this project.
                            </p>
                        </div>


                        <div className="mfg-faq-wrapper">

                            <Accordion
                                open={openFaq}
                                toggle={toggleFaq}
                            >

                                {faqs.map((faq) => (
                                    <AccordionItem
                                        key={faq.id}
                                    >
                                        <AccordionHeader
                                            targetId={faq.id}
                                        >
                                            {faq.question}
                                        </AccordionHeader>

                                        <AccordionBody
                                            accordionId={faq.id}
                                        >
                                            {faq.answer}
                                        </AccordionBody>
                                    </AccordionItem>
                                ))}

                            </Accordion>

                            <div className="mfg-hiring-note">
                                <FiBriefcaseFallback />

                                <div>
                                    <strong>
                                        Looking to hire candidates?
                                    </strong>

                                    <p>
                                        Yes, we specialize in helping
                                        businesses find and hire the right
                                        talent. Our HR and recruitment
                                        services are tailored to meet your
                                        company’s specific hiring needs.
                                        Contact us to learn how we can
                                        support your hiring process.
                                    </p>

                                    <a
                                        href="https://wocons.com/"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        WoCons – Custom AI & CAD Solutions |
                                        Bengaluru
                                        <FiArrowRight />
                                    </a>
                                </div>
                            </div>

                        </div>

                    </Container>
                </section>


                {/* ================= RELATED PROJECTS ================= */}

                <section className="mfg-section mfg-related-section">
                    <Container>

                        <div className="mfg-section-heading">
                            <span>EXPLORE MORE AI PROJECTS</span>
                            <h2>
                                Continue Exploring AI & ML Projects
                            </h2>
                            <p>
                                Explore other practical AI and machine
                                learning projects available through
                                Feesworth.
                            </p>
                        </div>


                        <Row className="g-4">

                            {relatedProjects.map((project, index) => (
                                <Col
                                    key={index}
                                    md="6"
                                    lg="3"
                                >
                                    <Card className="mfg-related-card">
                                        <CardBody>

                                            <div className="mfg-related-icon">
                                                {project.icon}
                                            </div>

                                            <h3>
                                                {project.title}
                                            </h3>

                                            <p>
                                                {project.description}
                                            </p>

                                            <a
                                                href={project.link}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Course Page
                                                <FiArrowRight />
                                            </a>

                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}

                        </Row>

                    </Container>
                </section>


                {/* ================= CONTACT ================= */}

                <section
                    className="mfg-contact-section"
                    id="mfg-contact"
                >
                    <Container>

                        <div className="mfg-contact-heading">

                            <span>
                                READY TO BUILD?
                            </span>

                            <h2>
                                Let's Discuss Your Manufacturing AI Project
                            </h2>

                            <p>
                                Have questions about the project, tools,
                                prerequisites, deployment, or training?
                                Send us your details and our team will get
                                back to you.
                            </p>

                        </div>


                        <div className="mfg-contact-wrapper">

                            <GetInTouch
                                courseName="Intelligent Manufacturing Quality Control System For Tire Production"
                                title="Get in Touch"
                                subtitle="Interested in building this AI-powered manufacturing quality control project? Send us your details and our team will contact you."
                            />

                        </div>

                    </Container>
                </section>

            </div>

        </>

    );
};


/*
 * Small local icon wrapper so the FAQ section does not require
 * another icon package.
 */
const FiBriefcaseFallback = () => (
    <FiServer />
);


export default IntelligentManufacturingQualityControlPage;